import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Person, VideoCall, EventAvailable, School } from '@mui/icons-material';

/**
 * Student-scoped row for a single class — presentation only.
 *
 * Matches the themed mockup's `.row` exactly: a borderless grid row
 * (`auto 1fr auto`, gap 16, padding 15px 4px) separated from siblings by a
 * bottom divider (none on the last). No card border, radius, or hover-lift.
 * Every value, capability flag and handler is supplied by StudentDashboard;
 * this component performs no time math, status mapping, or API/dialog work.
 *
 * `dateLabel` is optional: supplied for the Upcoming list ("Tomorrow · Jul 11"),
 * omitted for Today's list. The shared `RingButton` is passed pre-wired via
 * `ringButton` so its data wiring stays in the dashboard.
 */
const PALETTE_KEYS = ['primary', 'secondary', 'success', 'warning', 'error', 'info'];

const TodaysClassesCard = ({
  subject,
  teacherName,
  statusLabel,
  statusColor,
  dateLabel,
  time,
  duration,
  room,
  icon,
  canJoin,
  canReschedule,
  onJoin,
  onReschedule,
  ringButton,
}) => {
  const showActions = canJoin || canReschedule;
  const tagColor = PALETTE_KEYS.includes(statusColor) ? statusColor : 'primary';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        alignItems: 'center',
        gap: '16px',
        px: '4px',
        py: '15px',
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      {/* Subject icon tile — .subic */}
      <Box
        sx={{
          width: 46,
          height: 46,
          flex: 'none',
          borderRadius: '12px',
          display: 'grid',
          placeItems: 'center',
          color: 'primary.main',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.13),
          '& svg': { fontSize: 18 },
        }}
      >
        {icon || <School />}
      </Box>

      {/* Middle — subject + status tag · teacher · (actions) */}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'text.primary' }}>
            {subject}
          </Typography>
          <Box
            component="span"
            sx={{
              fontSize: '11.5px',
              fontWeight: 700,
              padding: '3px 10px',
              borderRadius: '7px',
              lineHeight: 1.4,
              color: `${tagColor}.main`,
              bgcolor: (theme) => alpha(theme.palette[tagColor].main, 0.13),
            }}
          >
            {statusLabel}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: '2px' }}>
          <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography sx={{ color: 'text.secondary', fontSize: '12.5px' }}>
            {teacherName}
          </Typography>
        </Box>

        {showActions && (
          <Box sx={{ display: 'flex', gap: '8px', mt: 1, flexWrap: 'wrap' }}>
            {canJoin && (
              <Button
                variant="contained"
                size="small"
                startIcon={<VideoCall />}
                onClick={onJoin}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  px: '12px',
                  py: '7px',
                }}
              >
                Join
              </Button>
            )}
            {ringButton}
            {canReschedule && !canJoin && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EventAvailable />}
                onClick={onReschedule}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  px: '12px',
                  py: '7px',
                }}
              >
                Reschedule
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Right — .when / .tt */}
      <Box sx={{ textAlign: 'right', flex: 'none' }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary' }}>
          {dateLabel || time}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '12px', fontWeight: 500 }}>
          {dateLabel ? `${time} · ${duration} min` : `${duration} min · ${room}`}
        </Typography>
      </Box>
    </Box>
  );
};

export default TodaysClassesCard;
